/**
 * Gesture Utilities - Handles touch gestures and keyboard navigation
 * Provides swipe detection for mobile devices and keyboard navigation for desktop
 */
import { LOGGING } from '/settings.js';

function logGestures(message, data = null) {
    if (LOGGING?.ENABLED && (LOGGING?.DEBUG || LOGGING?.GENERAL)) {
        if (data !== null && data !== undefined) {
            console.log(`[GESTURES] ${message}`, data);
        } else {
            console.log(`[GESTURES] ${message}`);
        }
    }
}

// Track active keyboard handlers to prevent conflicts
let activeKeyboardHandlers = new Set();

/**
 * Check if a touch position is within the cards banner boundary (bottom 150px including safe area)
 * @param {number} clientY - Y coordinate of the touch
 * @returns {boolean} True if touch is in cards area
 */
function isInCardsBoundary(clientY) {
    const viewportHeight = window.innerHeight;
    // Cards boundary: 150px from bottom of viewport (includes safe area)
    // This matches the user's requirement: "150px from bottom (including android bar if there is one)"
    const cardsBoundary = 150;
    const boundaryTop = viewportHeight - cardsBoundary;
    return clientY >= boundaryTop;
}

/**
 * Enable swipe gestures (left/right) for a carousel element
 * @param {HTMLElement} element - The element to attach swipe detection to
 * @param {Function} onSwipeLeft - Callback when swiping left (next)
 * @param {Function} onSwipeRight - Callback when swiping right (previous)
 * @param {Object} options - Configuration options
 * @param {number} options.minSwipeDistance - Minimum distance in pixels to trigger swipe (default: 50)
 * @param {number} options.maxVerticalDistance - Maximum vertical distance to still count as horizontal swipe (default: 100)
 * @param {boolean} options.ignoreCardsBoundary - If true, ignore swipes in cards boundary area (default: false)
 * @returns {Function} Cleanup function to remove event listeners
 */
export function enableSwipeGestures(element, onSwipeLeft, onSwipeRight, options = {}) {
    if (!element || typeof onSwipeLeft !== 'function' || typeof onSwipeRight !== 'function') {
        console.warn('enableSwipeGestures: Invalid parameters');
        return () => { };
    }

    const {
        minSwipeDistance = 50,
        maxVerticalDistance = 100,
        ignoreCardsBoundary = false
    } = options;

    logGestures('enableSwipeGestures: init', { minSwipeDistance, maxVerticalDistance, ignoreCardsBoundary });

    let touchStartX = null;
    let touchStartY = null;
    let touchEndX = null;
    let touchEndY = null;
    let lastSwipeTime = 0;
    const SWIPE_DEBOUNCE_MS = 350;

    function handleTouchStart(e) {
        const firstTouch = e.touches[0];
        touchStartX = firstTouch.clientX;
        touchStartY = firstTouch.clientY;
        logGestures('touchstart', { x: touchStartX, y: touchStartY });
    }

    function handleTouchMove(e) {
        // Prevent scrolling while swiping horizontally
        if (touchStartX !== null) {
            const currentTouch = e.touches[0];
            touchEndX = currentTouch.clientX;
            touchEndY = currentTouch.clientY;

            const deltaX = Math.abs(touchEndX - touchStartX);
            const deltaY = Math.abs(touchEndY - touchStartY);

            // If horizontal movement is greater than vertical, prevent default scrolling
            if (deltaX > deltaY && deltaX > 10) {
                e.preventDefault();
                logGestures('touchmove: preventScroll', { deltaX, deltaY });
            }
            logGestures('touchmove', { endX: touchEndX, endY: touchEndY, deltaX, deltaY });
        }
    }

    function handleTouchEnd(e) {
        if (touchStartX === null) return;

        // If move handler didn't capture an end position (e.g., quick tap), grab it here
        if (touchEndX === null && e.changedTouches && e.changedTouches[0]) {
            touchEndX = e.changedTouches[0].clientX;
            touchEndY = e.changedTouches[0].clientY;
        }

        if (touchEndX === null) return;

        // Check if touch is in cards boundary and should be ignored
        if (ignoreCardsBoundary) {
            // Check both start and end positions - if either is in cards area, ignore the swipe
            const endY = touchEndY !== null ? touchEndY : (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : touchStartY);
            if (isInCardsBoundary(touchStartY) || isInCardsBoundary(endY)) {
                // Touch started or ended in cards area, ignore this swipe
                logGestures('touchend: ignored due to cards boundary', { startY: touchStartY, endY });
                touchStartX = null;
                touchStartY = null;
                touchEndX = null;
                touchEndY = null;
                return;
            }
        }

        const deltaX = touchStartX - touchEndX;
        const deltaY = touchStartY - touchEndY;
        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);

        // Check if it's a horizontal swipe (not vertical scrolling)
        if (absDeltaX > minSwipeDistance && absDeltaY < maxVerticalDistance) {
            const now = Date.now();
            if (now - lastSwipeTime >= SWIPE_DEBOUNCE_MS) {
                lastSwipeTime = now;
                if (deltaX > 0) {
                    // Swiped left (next)
                    logGestures('touchend: swipe LEFT (next)', { absDeltaX, absDeltaY });
                    onSwipeLeft();
                } else {
                    // Swiped right (previous)
                    logGestures('touchend: swipe RIGHT (prev)', { absDeltaX, absDeltaY });
                    onSwipeRight();
                }
            } else {
                logGestures('touchend: swipe suppressed by debounce', { absDeltaX, absDeltaY, sinceLast: now - lastSwipeTime, debounceMs: SWIPE_DEBOUNCE_MS });
            }
        } else {
            logGestures('touchend: no-swipe (thresholds not met)', { absDeltaX, absDeltaY, minSwipeDistance, maxVerticalDistance });
        }

        // Reset
        touchStartX = null;
        touchStartY = null;
        touchEndX = null;
        touchEndY = null;
    }

    function handleTouchCancel() {
        // Reset on cancel
        touchStartX = null;
        touchStartY = null;
        touchEndX = null;
        touchEndY = null;
        logGestures('touchcancel: reset');
    }

    // Add event listeners
    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    element.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    // Return cleanup function
    return () => {
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchmove', handleTouchMove);
        element.removeEventListener('touchend', handleTouchEnd);
        element.removeEventListener('touchcancel', handleTouchCancel);
        logGestures('enableSwipeGestures: cleanup listeners');
    };
}

/**
 * Enable keyboard navigation (arrow keys) for carousels
 * @param {Function} onKeyLeft - Callback when left arrow key is pressed (previous)
 * @param {Function} onKeyRight - Callback when right arrow key is pressed (next)
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether keyboard navigation is enabled (default: true)
 * @param {HTMLElement} options.target - Element to check if focused/active (default: document)
 * @param {Function} options.isActive - Function to check if carousel is active (default: always true)
 * @returns {Function} Cleanup function to remove event listeners
 */
export function enableKeyboardNavigation(onKeyLeft, onKeyRight, options = {}) {
    if (typeof onKeyLeft !== 'function' || typeof onKeyRight !== 'function') {
        console.warn('enableKeyboardNavigation: Invalid parameters');
        return () => { };
    }

    const {
        enabled = true,
        target = document,
        isActive = () => true
    } = options;

    if (!enabled) {
        return () => { };
    }

    // Create unique handler ID
    const handlerId = Symbol('keyboard-handler');
    activeKeyboardHandlers.add(handlerId);
    logGestures('enableKeyboardNavigation: init');

    function handleKeyDown(e) {
        // Only handle if this handler is active and carousel is active
        if (!activeKeyboardHandlers.has(handlerId) || !isActive()) {
            return;
        }

        // Check if user is typing in an input field
        const activeElement = document.activeElement;
        if (activeElement && (
            activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.isContentEditable
        )) {
            return;
        }

        // Handle arrow keys
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            e.stopPropagation();
            onKeyLeft();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            e.stopPropagation();
            onKeyRight();
        }
    }

    target.addEventListener('keydown', handleKeyDown, { passive: false });

    // Return cleanup function
    return () => {
        activeKeyboardHandlers.delete(handlerId);
        target.removeEventListener('keydown', handleKeyDown);
        logGestures('enableKeyboardNavigation: cleanup');
    };
}

/**
 * Enable both swipe gestures and keyboard navigation for a carousel
 * @param {HTMLElement} element - The element to attach swipe detection to
 * @param {Function} onNavigateLeft - Callback when navigating left (previous)
 * @param {Function} onNavigateRight - Callback when navigating right (next)
 * @param {Object} options - Configuration options
 * @param {boolean} options.enableSwipe - Enable swipe gestures (default: true)
 * @param {boolean} options.enableKeyboard - Enable keyboard navigation (default: true)
 * @param {Function} options.isActive - Function to check if carousel is active (for keyboard)
 * @param {Object} options.swipeOptions - Options for swipe gestures
 * @param {Object} options.keyboardOptions - Options for keyboard navigation
 * @returns {Function} Cleanup function to remove all event listeners
 */
export function enableCarouselNavigation(element, onNavigateLeft, onNavigateRight, options = {}) {
    if (!element || typeof onNavigateLeft !== 'function' || typeof onNavigateRight !== 'function') {
        console.warn('enableCarouselNavigation: Invalid parameters');
        return () => { };
    }

    const {
        enableSwipe = true,
        enableKeyboard = true,
        isActive = () => true,
        swipeOptions = {},
        keyboardOptions = {},
        ignoreCardsBoundary = false
    } = options;

    const cleanupFunctions = [];

    // Enable swipe gestures
    if (enableSwipe) {
        logGestures('enableCarouselNavigation: enabling swipe');
        const swipeCleanup = enableSwipeGestures(
            element,
            onNavigateRight, // Swipe left = next
            onNavigateLeft,  // Swipe right = previous
            {
                ...swipeOptions,
                ignoreCardsBoundary
            }
        );
        cleanupFunctions.push(swipeCleanup);
    }

    // Enable keyboard navigation
    if (enableKeyboard) {
        logGestures('enableCarouselNavigation: enabling keyboard');
        const keyboardCleanup = enableKeyboardNavigation(
            onNavigateLeft,
            onNavigateRight,
            {
                ...keyboardOptions,
                isActive: () => isActive() && element.isConnected
            }
        );
        cleanupFunctions.push(keyboardCleanup);
    }

    // Return combined cleanup function
    return () => {
        cleanupFunctions.forEach(cleanup => cleanup());
        logGestures('enableCarouselNavigation: cleanup all');
    };
}

/**
 * Disable all active keyboard handlers (useful for cleanup)
 */
export function disableAllKeyboardHandlers() {
    activeKeyboardHandlers.clear();
}

